import type { UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { isEmpty } from "lodash";

import type { IQuery } from "@citrine/client";
import {
  RefreshClient,
  getQueryCacheFromConfig,
  normalizeQuery,
} from "@citrine/client";

import type { IDataObjectOrTemplate } from "../data-object";

import type {
  GemdDataQuery,
  JsonApiResponse,
  MaterialAndMeasurements,
  MaterialAndMeasurementsPageResult,
  MaterialTag,
  MaterialsAndMeasurementsMetadata,
  MaterialsQueryParams,
  SampleCountResponse,
  SortEntry,
  TerminalMaterialAttribute,
} from "./data-query";
import { MATERIAL_ATTRIBUTE_QUERY } from "./data-query";

type AttributeType = "property" | "condition";

/** JSON:API sort string: comma-separated fields, leading "-" for descending. */
export function serializeSort(entries: SortEntry[]): string {
  return entries
    .map(({ field, order }) => `${order === "desc" ? "-" : ""}${field}`)
    .join(",");
}

interface MaterialAttributeQuery extends IQuery {
  dataset_ids?: string[];
  is_material_consumed?: boolean;
  is_material_produced?: boolean;
  attribute_type?: AttributeType;
}

export class TeamDataQueryClient extends RefreshClient {
  readonly endpoint: string;
  readonly endpointV2: string;

  protected constructor(public readonly teamId: string) {
    super();
    this.endpoint = `/api/v1/teams/${teamId}/gemd/query`;
    this.endpointV2 = `/api/v2/teams/${teamId}/gemd/query`;
  }

  static getInstance(teamId: string) {
    return new this(teamId);
  }

  async find(query: GemdDataQuery) {
    const result = await this.request<
      {
        item: IDataObjectOrTemplate;
        metadata?: MaterialsAndMeasurementsMetadata;
      }[]
    >(this.findStorablesConfig(query));
    return result.map((data) => data.item);
  }

  useFind(
    query: GemdDataQuery,
    options?: Omit<
      UseQueryOptions<IDataObjectOrTemplate[]>,
      "queryKey" | "queryFn"
    >,
  ) {
    return useQuery({
      ...options,
      queryKey: getQueryCacheFromConfig(this.findStorablesConfig(query)),
      queryFn: () => this.find(query),
    });
  }

  async findWithMeasurementsPage(
    query: GemdDataQuery,
    params: MaterialsQueryParams,
  ): Promise<MaterialAndMeasurementsPageResult> {
    const response = await this.request<
      JsonApiResponse<MaterialAndMeasurements>
    >(this.findWithMeasurementsPageConfig(query, params));
    return {
      materials: response.data.map((resource) => resource.attributes),
      meta: { page: response.meta.page },
    };
  }

  useFindWithMeasurementsPage(
    query: GemdDataQuery,
    params: MaterialsQueryParams,
    options?: Omit<
      UseQueryOptions<MaterialAndMeasurementsPageResult, AxiosError>,
      "queryKey" | "queryFn"
    >,
  ): UseQueryResult<MaterialAndMeasurementsPageResult, AxiosError> {
    const config = this.findWithMeasurementsPageConfig(query, params);
    return useQuery({
      ...options,
      queryKey: getQueryCacheFromConfig(config),
      queryFn: () => this.findWithMeasurementsPage(query, params),
    });
  }

  getSampleCount(query: GemdDataQuery) {
    return this.request<SampleCountResponse>({
      method: "POST",
      url: `${this.endpoint}/material-histories-summary`,
      data: query,
      // TODO: These params are now deprecated and instaed should use the new
      // "created_at_criteria".  Need to update the code where this is used to
      // append the appropriate criteria.
      // https://citrine.atlassian.net/browse/PNE-8909
      params: {
        creation_from: query.creation_from,
        creation_to: query.creation_to,
      },
    });
  }

  useGetSampleCount(
    query: GemdDataQuery,
    options?: Omit<
      UseQueryOptions<SampleCountResponse, AxiosError>,
      "queryKey" | "queryFn"
    >,
  ) {
    return useQuery<SampleCountResponse, AxiosError>({
      ...options,
      queryKey: [
        `${this.endpoint}/material-histories-summary`,
        query.datasets,
        query.criteria,
      ],
      queryFn: () => this.getSampleCount(query),
      // avoid polluting logs in unit tests when teamId is undefined
      enabled: Boolean(this.teamId && options?.enabled !== false),
    });
  }

  useGetMaterialAttributes(
    query?: MaterialAttributeQuery,
    options?: Omit<
      UseQueryOptions<TerminalMaterialAttribute[], Error>,
      "queryKey" | "queryFn"
    >,
  ) {
    const dataset_ids = query?.dataset_ids
      ? [...query.dataset_ids].sort()
      : undefined;
    const config = {
      method: "GET",
      url: `${this.endpoint}/material-attributes`,
      params: normalizeQuery({ ...query, dataset_ids }),
    };

    return useQuery({
      ...options,
      queryKey: getQueryCacheFromConfig(config),
      queryFn: () => this.request<TerminalMaterialAttribute[]>(config),
    });
  }

  async getMaterialAttributesForMultipleTypes({
    datasets,
    materialTypes,
    attribute_type,
  }: {
    datasets: string[];
    materialTypes: (keyof typeof MATERIAL_ATTRIBUTE_QUERY)[];
    attribute_type?: AttributeType;
  }) {
    const configs = materialTypes.map((type) => {
      switch (type) {
        case "isolated":
          return this.materialAttributesConfig({
            dataset_ids: datasets,
            attribute_type,
            ...MATERIAL_ATTRIBUTE_QUERY.isolated,
          });
        case "raw_and_isolated":
          return this.materialAttributesConfig({
            dataset_ids: datasets,
            attribute_type,
            ...MATERIAL_ATTRIBUTE_QUERY.raw_and_isolated,
          });
        default:
          return this.materialAttributesConfig({
            dataset_ids: datasets,
            attribute_type,
            ...MATERIAL_ATTRIBUTE_QUERY.terminal,
          });
      }
    });

    const responses = await Promise.all(
      configs.map((config) =>
        this.request<TerminalMaterialAttribute[]>(config),
      ),
    );
    return responses.flat();
  }

  useGetMaterialAttributesForMultipleTypes<
    Selection = TerminalMaterialAttribute[],
  >(
    props: {
      datasets: string[];
      materialTypes: (keyof typeof MATERIAL_ATTRIBUTE_QUERY)[];
      attribute_type?: AttributeType;
    },
    options?: Omit<
      UseQueryOptions<TerminalMaterialAttribute[], Error, Selection>,
      "queryKey" | "queryFn"
    >,
  ) {
    const dataset_ids = props.datasets.toSorted();
    return useQuery({
      ...options,
      queryKey: [
        `${this.endpoint}/material-attributes`,
        normalizeQuery({ ...props, dataset_ids }),
      ],
      queryFn: () => this.getMaterialAttributesForMultipleTypes(props),
    });
  }

  getConditionsForProperty({
    propertyTemplateId,
    datasetIds,
  }: {
    propertyTemplateId: string;
    datasetIds?: string[];
  }) {
    return this.request<TerminalMaterialAttribute[]>({
      method: "GET",
      url: `${this.endpointV2}/conditions-for-property`,
      params: normalizeQuery({
        property_template_id: propertyTemplateId,
        dataset_ids: datasetIds,
      }),
    });
  }

  useGetConditionsForProperty<Selection = TerminalMaterialAttribute[]>(
    props: {
      propertyTemplateId: string | undefined;
      datasetIds?: string[];
    },
    options?: Omit<
      UseQueryOptions<TerminalMaterialAttribute[], Error, Selection>,
      "queryKey" | "queryFn" | "enabled"
    >,
  ) {
    const { propertyTemplateId, datasetIds } = props;
    const sortedDatasetIds = datasetIds?.toSorted();
    return useQuery({
      ...options,
      enabled: Boolean(propertyTemplateId),
      queryKey: [
        `${this.endpointV2}/conditions-for-property`,
        normalizeQuery({
          property_template_id: propertyTemplateId,
          dataset_ids: sortedDatasetIds,
        }),
      ],
      queryFn: () =>
        propertyTemplateId === undefined
          ? Promise.resolve<TerminalMaterialAttribute[]>([])
          : this.getConditionsForProperty({
              propertyTemplateId,
              datasetIds: sortedDatasetIds,
            }),
    });
  }

  getMaterialTags(datasetIds: string[]) {
    return this.request<MaterialTag[]>({
      method: "GET",
      url: `${this.endpoint}/material-tags`,
      params: { dataset_ids: datasetIds },
    });
  }

  useGetMaterialTags(
    datasetIds: string[],
    options?: Omit<UseQueryOptions<MaterialTag[]>, "queryKey" | "queryFn">,
  ): UseQueryResult<MaterialTag[]> {
    const sortedDatasetIds = datasetIds.toSorted();
    return useQuery({
      ...options,
      queryKey: [
        `${this.endpoint}/material-tags`,
        JSON.stringify(sortedDatasetIds),
      ],
      queryFn: () => this.getMaterialTags(sortedDatasetIds),
    });
  }

  protected findStorablesConfig({
    creation_from,
    creation_to,
    ...query
  }: GemdDataQuery) {
    return {
      method: "POST",
      url: `${this.endpoint}/storables`,
      data: normalizeQuery(query),
      params: {
        creation_from,
        creation_to,
        per_page: 0,
      },
    };
  }

  protected findWithMeasurementsPageConfig(
    { creation_from, creation_to, ...query }: GemdDataQuery,
    params: MaterialsQueryParams,
  ) {
    const sortEntries = params.sort ?? [];

    return {
      url: `/api/v2/teams/${this.teamId}/gemd/query/materials-and-measurements`,
      method: "POST",
      data: normalizeQuery({
        ...query,
        ...(query?.datasets ? { datasets: [...query.datasets].sort() } : {}),
      }),
      params: normalizeQuery({
        creation_from,
        creation_to,
        "page[number]": params.pageNumber,
        "page[size]": params.pageSize,
        ...(isEmpty(sortEntries) ? {} : { sort: serializeSort(sortEntries) }),
      }),
    };
  }

  protected materialAttributesConfig(query?: MaterialAttributeQuery) {
    const dataset_ids = query?.dataset_ids
      ? [...query.dataset_ids].sort()
      : undefined;
    return {
      method: "GET",
      url: `${this.endpoint}/material-attributes`,
      params: normalizeQuery({ ...query, dataset_ids }),
    };
  }
}
